import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { WORKSPACE_CLIENT_KEY, ACTIVE_CLIENT_KEY, LEGACY_CM_KEY } from '../workspace/storage.js';
import { acquireIdToken, redirectResult } from './msal.js';
import { API_BASE } from '../http/apiBase.js';

const AuthContext = createContext(null);

// One exchange for both SSO legs (popup resolves in-page, redirect resolves on the next
// page load): Microsoft ID token in, Orbit session token stored, user out.
async function exchangeSsoToken(idToken) {
  const r = await fetch(`${API_BASE}/portal/login/sso`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ idToken }),
  });
  let data = {};
  try { data = await r.json(); } catch {}
  if (!r.ok) throw new Error(data.error || `Sign-in failed (${r.status})`);
  localStorage.setItem('mp_token', data.token);
  return data.user;
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      // A page load can be the return leg of a redirect-flow Microsoft sign-in (the
      // fallback for blocked popups — see msal.js). That resolves to an ID token here,
      // which finishes the same exchange the popup path runs. On failure the message is
      // parked for LoginPage, because this leg has no login screen to show it on yet.
      const idToken = await redirectResult;
      if (idToken) {
        try {
          const u = await exchangeSsoToken(idToken);
          setUser(u);
          return;
        } catch (err) {
          try { sessionStorage.setItem('mp_sso_error', err.message); } catch {}
        }
      }

      const token = localStorage.getItem('mp_token');
      if (!token) return;
      const r = await fetch(`${API_BASE}/portal/me`,
                            { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => null);
      if (r?.ok) setUser(await r.json());
      else localStorage.removeItem('mp_token');
    })().finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username, password) => {
    const r = await fetch(`${API_BASE}/portal/login`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ username, password }),
    });
    let data = {};
    try { data = await r.json(); } catch {}
    if (!r.ok) throw new Error(data.error || `Login failed (${r.status})`);
    localStorage.setItem('mp_token', data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // Microsoft SSO: MSAL produces the ID token (see msal.js); this exchanges it for the same
  // session token password login yields, so past this point the two flows are identical.
  const loginSso = useCallback(async () => {
    const idToken = await acquireIdToken();
    const u = await exchangeSsoToken(idToken);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('mp_token');
    setUser(null);
    history.pushState(null, '', '/login');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  // ── Impersonation ───────────────────────────────────────────────────────────
  //
  // Both directions swap the token and then reload the browser outright rather than moving
  // React state. Who you are is an input to almost everything already fetched — the Hub's
  // tiles, the workspace list, every module's in-memory cache — and there is no reliable way
  // to invalidate all of it from here. A reload is the one operation guaranteed to leave
  // nothing behind from the previous identity.
  //
  // The stored workspace goes with it. Client access is per user and per module, so the
  // administrator's last-used client is very often one the target cannot open; carrying it
  // over would land them in a workspace the server then refuses, which reads as the feature
  // being broken rather than as a stale preference.
  const swapSession = useCallback((token) => {
    localStorage.setItem('mp_token', token);
    try {
      // LEGACY_CM_KEY too: readStoredClientId still falls back to it, so leaving it behind
      // would carry the administrator's Competitive Monitoring client into the session.
      [WORKSPACE_CLIENT_KEY, ACTIVE_CLIENT_KEY, LEGACY_CM_KEY]
        .forEach(k => localStorage.removeItem(k));
    } catch { /* storage disabled — nothing to clear */ }
    window.location.href = '/';
  }, []);

  const impersonate = useCallback(async (userId) => {
    const token = localStorage.getItem('mp_token');
    const r = await fetch(`${API_BASE}/portal/impersonate/${userId}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    let data = {};
    try { data = await r.json(); } catch {}
    if (!r.ok) throw new Error(data.error || `Could not impersonate (${r.status})`);
    swapSession(data.token);
  }, [swapSession]);

  const stopImpersonating = useCallback(async () => {
    const token = localStorage.getItem('mp_token');
    const r = await fetch(`${API_BASE}/portal/impersonate`, {
      method:  'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    let data = {};
    try { data = await r.json(); } catch {}
    // The way back is the only exit, so a failure here cannot just surface an error and leave
    // the admin signed in as somebody else. Drop the session and make them log in again.
    if (!r.ok) { logout(); return; }
    swapSession(data.token);
  }, [swapSession, logout]);

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, login, loginSso, logout,
      // Truthy only while impersonating — `actingAs` is the administrator's id, carried on the
      // token and echoed by /portal/me.
      impersonating: !!user?.actingAs,
      impersonatorName: user?.actingAsName || null,
      impersonate, stopImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
